// Copyright 2023 Sorama B.V.
import { autoinject } from "aurelia-dependency-injection";
import { RecordingGroupContainer } from "datatypes/Containers";
import { GenericMeasurement, RepresentativeDTO } from "datatypes/SoramaWebApi";
import { MeasurementRepository, MeasurementUpdateField } from "repositories/MeasurementRepository";
import { IStorageService } from "services/Storage/IStorageService";
import { RecordingGroupContainerStorageService } from "services/Storage/RecordingGroupContainerStorageService";
import { IMeasurementStorageService, SyncStatus } from "./IMeasurementStorageService";

/** "Cloud" is the portal azure hosting platform with the following storages:
 *  - SQL database stores a list of ids
 *  - MongoDB stores the metadata (name, projectId, start, duration, distance etc.)
 *  - Azure blob storage stores the actual data
 * "Local" is on the PC file system, contains both the measurement data (in recording containers),
 * and metadata (as json in recordingGroup containers)
 */
export type Location = "Local" | "Cloud";

/**
 * Registered means the measurement id is in the sql database and has metadata in mongoDB
 * Uploaded means the data is uploaded to the cloud blob storage
 *
 * (orphan) measurement is local on this pc and not yet registered in cloud
 * (local) measurement is local on this pc and registered in cloud
 * (both) measurement is local on this pc and uploaded to cloud
 * (cloud) measurement is not local on this pc and uploaded to cloud
 * (remote) measurement is not local on this pc and registered to cloud
 */
export type MeasurementType = "Orphan" | "Local" | "Both" | "Cloud" | "Remote";

export type MeasurementOperation = "delete" | "upload" | "transfer" | "export";

export type UpdateableMeasurementField = "name" | "notes" | "distance" | "metadata";

// A helper service that syncing the cloud measurements on the local container
// NOTE: This class must be defined before Measurement class because it's used in its constructor
@autoinject
export class StorageContainerService {
    constructor(private readonly storageService: IStorageService) {}

    // Add the projectIds and measurementIds to the container metadata
    public async addCloudMeasurementToContainerMetadata(
        containerId: string,
        measurementId: string,
        projectId: string
    ): Promise<void> {
        const measurementIds: string[] = await this.storageService.getMetadataValue(containerId, "measurementIds");
        const projectIds: string[] = await this.storageService.getMetadataValue(containerId, "projectIds");

        if (measurementIds.includes(measurementId)) {
            throw new Error("This measurement has already been registered in the cloud");
        }

        measurementIds.push(measurementId);
        projectIds.push(projectId);

        await this.storageService.updateMetadata(containerId, "measurementIds", measurementIds);
        await this.storageService.updateMetadata(containerId, "projectIds", projectIds);
    }

    // Remove the projectIds and measurementIds from the container metadata
    public async removeCloudMeasurementFromContainerMetadata(
        containerId: string,
        measurementId: string,
        projectId: string
    ): Promise<void> {
        if (!containerId) return;
        const measurementIds: string[] = await this.storageService.getMetadataValue(containerId, "measurementIds");
        const projectIds: string[] = await this.storageService.getMetadataValue(containerId, "projectIds");

        const newMeasurementIds = measurementIds.filter((id) => id !== measurementId);
        const filteredProjectIds = projectIds.filter((id) => id !== projectId);

        await this.storageService.updateMetadata(containerId, "measurementIds", newMeasurementIds);
        await this.storageService.updateMetadata(containerId, "projectIds", filteredProjectIds);
    }
}

@autoinject
export abstract class Measurement {
    public abstract readonly type: MeasurementType;
    public abstract readonly locationString: string;
    public abstract readonly dataLocation: Set<Location>;
    public abstract readonly origin: Set<Location>;
    public abstract readonly allowedOperations: Set<MeasurementOperation>;

    protected localContainer?: RecordingGroupContainer;
    public setLocalContainer(container: RecordingGroupContainer) {
        this.localContainer = container;
    }
    protected cloudMeasurement?: GenericMeasurement;
    public setCloudMeasurement(measurement: GenericMeasurement) {
        this.cloudMeasurement = measurement;
    }

    public get id(): string {
        return this.cloudId || this.containerId;
    }
    public set id(value: string) {
        if (!this.cloudMeasurement) throw new Error("Cannot change the id of an only local measurement");
        this.cloudMeasurement.id = value;
    }

    public get cloudId(): string {
        return this.cloudMeasurement?.id;
    }

    public get projectId(): string {
        return this.cloudMeasurement?.projectId;
    }

    public get containerId(): string {
        return this.localContainer?.id;
    }
    // The unique SegmentUUID of the measurement mkv file (.sor). This is stored in the GenericMeasurement in the cloud when a sor file is imported
    public get sorId(): string {
        return this.cloudMeasurement?.sorId;
    }

    public get created(): Date {
        return this.cloudMeasurement?.created || this.localContainer?.created;
    }
    public get start(): Date {
        return this.cloudMeasurement?.start || this.localContainer?.created;
    }
    public get duration(): number {
        return this.cloudMeasurement?.duration || this.localContainer?.metadata.sampleRate;
    }

    public get name(): string {
        return this.cloudMeasurement?.name || this.localContainer?.metadata.name;
    }
    public get notes(): string {
        return this.cloudMeasurement?.notes || this.localContainer?.metadata.notes;
    }
    public get distance(): number {
        return this.cloudMeasurement?.distance || this.localContainer?.metadata.distance;
    }
    public get metadata(): unknown {
        return this.cloudMeasurement?.metadata || this.localContainer?.metadata.metadata;
    }

    protected exportSpecialMeasurementAsJson(): File {
        const jsonString = JSON.stringify(this.cloudMeasurement.specialMeasurementInfo, null, 2);
        const jsonBlob = new Blob([jsonString], { type: "application/json" });
        const fileName = this.generateFileName(".json");
        return new File([jsonBlob], fileName, { type: "application/json" });
    }

    protected async exportSorFileFromLocal(): Promise<File> {
        const sorFileName = this.generateFileName(".sor");
        const blob = await this.storageService.getSor(this.sorId);
        return new File([blob], sorFileName);
    }

    protected async exportSorFileFromCloud(): Promise<File> {
        const sorFileName = this.generateFileName(".sor");
        const blob = await this.measurementRepository.getSorFile(this.cloudId);
        return new File([blob], sorFileName);
    }

    protected generateFileName(extension: string): string {
        return this.name.endsWith(extension) ? this.name : this.name + extension;
    }

    public async setProperty(property: UpdateableMeasurementField, value: unknown): Promise<void> {
        if (value === null || value === undefined) throw new Error("Cannot set a property to null or undefined");

        if (this.cloudMeasurement) {
            await this.measurementRepository.updateMeasurement(
                this.cloudMeasurement,
                MeasurementUpdateField.fromString(property),
                value
            );
        } else if (this.localContainer) {
            // Only allow changing the local measurement info when it is not registered in the cloud
            await this.recordingGroupContainerStorageService
                .updateKnownMetadata(this.containerId, property, value)
                .then(() => {
                    this.localContainer.metadata[property] = value as never;
                });
        }
    }

    private _size = 0;
    public get size(): number {
        if (this.isUploaded || this.isSpecialMeasurement) {
            if (this._size === 0) {
                const jsonString = JSON.stringify(this.cloudMeasurement.specialMeasurementInfo);
                this._size = new Blob([jsonString]).size;
            }
            return this._size;
        }

        if (this.isUploaded || this.isSorMeasurement) {
            return this.measurementRepository.getTotalMeasurementSize(this.cloudMeasurement);
        }

        return this._size;
    }
    public set size(value: number) {
        this._size = value;
    }

    public get ownerId() {
        return this.cloudMeasurement?.ownerId;
    }
    public owner?: RepresentativeDTO;

    public get isIncomplete(): boolean {
        return !this.origin.has("Cloud") && this.localContainer.metadata.projectIds.length === 0;
    }
    public get isUploaded(): boolean {
        return this.dataLocation.has("Cloud");
    }
    public get isAvailable(): boolean {
        return this.type !== "Remote";
    }
    public get isSorMeasurement(): boolean {
        return !!this.cloudMeasurement?.sorId;
    }
    public get hasSorFileInCloud(): boolean {
        return this.cloudMeasurement?.hasUploadedRawSorFile;
    }
    public get isSpecialMeasurement(): boolean {
        return !!(this.cloudMeasurement?.specialMeasurementType && this.cloudMeasurement?.specialMeasurementInfo);
    }

    public get fullOwnerName(): string {
        if (!this.owner) return "";
        if (!this.owner.FirstName && !this.owner.LastName) return this.owner.UserName;
        return [this.owner.FirstName, this.owner.LastName].join(" ");
    }

    public get canAnalyse(): boolean {
        const canAnalyseCloud = this.dataLocation.has("Cloud") && this.cloudMeasurement?.parts?.length > 0;
        const canAnalyseLocal = this.dataLocation.has("Local");
        return canAnalyseCloud || canAnalyseLocal;
    }

    constructor(
        protected readonly measurementRepository: MeasurementRepository,
        protected readonly measurementStorageService: IMeasurementStorageService,
        protected readonly storageContainerService: StorageContainerService,
        protected readonly storageService: IStorageService,
        protected readonly recordingGroupContainerStorageService: RecordingGroupContainerStorageService
    ) {}

    public abstract delete(projectId: string): Promise<void>;
    public abstract upload(projectId: string): Promise<void>;
    public abstract transfer(oldProjectId: string, newProjectId: string): Promise<void>;
    public abstract export(): Promise<File>;
}

export class OrphanMeasurement extends Measurement {
    public readonly type = "Orphan";
    public readonly locationString = "Local";
    public readonly dataLocation: Set<Location> = new Set(["Local"]);
    public readonly origin: Set<Location> = new Set(["Local"]);
    public readonly allowedOperations: Set<MeasurementOperation> = new Set(["transfer", "export"]);

    public async delete(): Promise<void> {
        throw new Error("Cannot delete a local measurement, please remove it from the file system manually");
    }
    public async upload(): Promise<void> {
        throw new Error("Cannot upload a measurement that is not added to a project");
    }

    public async transfer(oldProjectId: string, newProjectId: string): Promise<void> {
        // Just register the measurement in the cloud for the new project
        if (this.isSorMeasurement) {
            this.cloudMeasurement = await this.measurementStorageService.registerLocalSor(
                this.sorId,
                newProjectId,
                this.name
            );
        } else {
            // createGenericMeasurement already updates the storage container metadata
            const newMeasurementId = await this.measurementRepository.createGenericMeasurement(
                this.localContainer.id,
                newProjectId
            );
            this.cloudMeasurement = await this.measurementRepository.getMeasurement(newMeasurementId);
        }
    }

    public async export(): Promise<File> {
        if (this.isSpecialMeasurement) {
            return this.exportSpecialMeasurementAsJson();
        }
        throw new Error("Cannot export an orphan measurement");
    }
}

export class LocalMeasurement extends Measurement {
    public readonly type = "Local";
    public readonly locationString = "Local";
    public readonly dataLocation: Set<Location> = new Set(["Local"]);
    public readonly origin: Set<Location> = new Set(["Local", "Cloud"]);
    public readonly allowedOperations: Set<MeasurementOperation> = new Set(["delete", "upload", "transfer", "export"]);

    public setCloudMeasurement(measurement: GenericMeasurement): void {
        super.setCloudMeasurement(measurement);
    }

    public async delete(projectId: string): Promise<void> {
        await this.measurementRepository.deleteMeasurement(this.cloudId);
        await this.storageContainerService.removeCloudMeasurementFromContainerMetadata(
            this.containerId,
            this.cloudId,
            projectId
        );
    }

    public async upload(): Promise<void> {
        await this.measurementStorageService.setSyncStatus(SyncStatus.Upload, {
            id: this.cloudId,
            name: this.name,
            sorId: this.cloudMeasurement?.sorId,
        });
    }

    public async transfer(oldProjectId: string, newProjectId: string): Promise<void> {
        if (oldProjectId === newProjectId) throw new Error("Cannot transfer to the same project");

        if (oldProjectId) {
            // Remove from old project
            await this.measurementRepository.deleteMeasurement(this.cloudId);
            await this.storageContainerService.removeCloudMeasurementFromContainerMetadata(
                this.containerId,
                this.cloudId,
                oldProjectId
            );
        }

        if (this.isSorMeasurement) {
            this.cloudMeasurement = await this.measurementStorageService.registerLocalSor(
                this.sorId,
                newProjectId,
                this.name
            );
        } else {
            // Add to new project (createGenericMeasurement already updates the storage container metadata)
            const newMeasurementId = await this.measurementRepository.createGenericMeasurement(
                this.containerId,
                newProjectId
            );
            this.cloudMeasurement = await this.measurementRepository.getMeasurement(newMeasurementId);
        }
    }

    public async export(): Promise<File> {
        if (this.isSpecialMeasurement) {
            return this.exportSpecialMeasurementAsJson();
        }
        if (this.sorId) {
            return await this.exportSorFileFromLocal();
        }
        const blob = await this.storageService.getDataZip([this.containerId]);
        return new File([blob], this.name + ".zip");
    }
}

export class BothMeasurement extends Measurement {
    public readonly type = "Both";
    public readonly locationString = "Local & Cloud";
    public readonly dataLocation: Set<Location> = new Set(["Local", "Cloud"]);
    public readonly origin: Set<Location> = new Set(["Local", "Cloud"]);
    public readonly allowedOperations: Set<MeasurementOperation> = new Set(["delete", "transfer", "export"]);

    public async delete(projectId: string): Promise<void> {
        await this.measurementRepository.deleteMeasurement(this.cloudId);
        await this.storageContainerService.removeCloudMeasurementFromContainerMetadata(
            this.containerId,
            this.cloudId,
            projectId
        );
    }

    public async upload(): Promise<void> {
        throw new Error("Cannot upload a measurement that is already uploaded");
    }

    public async transfer(oldProjectId: string, newProjectId: string): Promise<void> {
        if (oldProjectId === newProjectId) throw new Error("Cannot transfer to the same project");

        // Cloning it will create a new id
        const newMeasurementId = await this.measurementRepository.cloneMeasurement(this.cloudId, newProjectId);

        if (oldProjectId) {
            // delete the measurement with the old id
            await this.measurementRepository.deleteMeasurement(this.cloudId);
            await this.storageContainerService.removeCloudMeasurementFromContainerMetadata(
                this.containerId,
                this.cloudId,
                oldProjectId
            );
        }

        // Set the new id so that it it used when adding the new container metadata
        this.id = newMeasurementId;

        if (this.localContainer) {
            await this.storageContainerService.addCloudMeasurementToContainerMetadata(
                this.containerId,
                this.cloudId,
                newProjectId
            );
        }
    }

    public async export(): Promise<File> {
        if (this.isSpecialMeasurement) {
            return this.exportSpecialMeasurementAsJson();
        }
        if (this.sorId) {
            return await this.exportSorFileFromLocal();
        }
        const blob = await this.storageService.getDataZip([this.containerId]);
        return new File([blob], this.name + ".zip");
    }
}

export class CloudMeasurement extends Measurement {
    public readonly type = "Cloud";
    public readonly locationString = "Cloud";
    public readonly dataLocation: Set<Location> = new Set(["Cloud"]);
    public readonly origin: Set<Location> = new Set(["Cloud"]);

    public get allowedOperations(): Set<MeasurementOperation> {
        const allowedOperationsSet = new Set<MeasurementOperation>(["delete", "transfer"]);

        // Allow export for SOR measurements or special measurements with uploaded files
        if ((this.isSorMeasurement && this.hasSorFileInCloud) || this.isSpecialMeasurement) {
            allowedOperationsSet.add("export");
        }

        return allowedOperationsSet;
    }

    public async delete(): Promise<void> {
        await this.measurementRepository.deleteMeasurement(this.cloudId);
    }

    public async upload(): Promise<void> {
        throw new Error("Cannot upload a measurement that is already uploaded");
    }

    public async transfer(oldProjectId: string, newProjectId: string): Promise<void> {
        if (oldProjectId === newProjectId) throw new Error("Cannot transfer to the same project");
        const newMeasurementId = await this.measurementRepository.cloneMeasurement(this.cloudId, newProjectId);
        await this.measurementRepository.deleteMeasurement(this.cloudId);
        this.id = newMeasurementId;
    }

    public async export(): Promise<File> {
        if (this.isSpecialMeasurement) {
            return this.exportSpecialMeasurementAsJson();
        }
        if (this.hasSorFileInCloud) {
            return await this.exportSorFileFromCloud();
        }
        throw new Error("Cannot download a cloud measurement that is not a special measurement or sor measurement");
    }
}

export class RemoteMeasurement extends Measurement {
    public readonly type = "Remote";
    public readonly locationString = "Remote";
    public readonly dataLocation: Set<Location> = new Set();
    public readonly origin: Set<Location> = new Set(["Cloud"]);
    public readonly allowedOperations: Set<MeasurementOperation> = new Set(["delete", "transfer"]);

    public async delete(): Promise<void> {
        await this.measurementRepository.deleteMeasurement(this.cloudId);
    }

    public async upload(): Promise<void> {
        throw new Error("Cannot upload a measurement that is not stored locally on this computer");
    }

    public async transfer(oldProjectId: string, newProjectId: string): Promise<void> {
        if (oldProjectId === newProjectId) throw new Error("Cannot transfer to the same project");
        const newMeasurementId = await this.measurementRepository.cloneMeasurement(this.cloudId, newProjectId);
        await this.measurementRepository.deleteMeasurement(this.cloudId);
        this.id = newMeasurementId;
    }

    public async export(): Promise<File> {
        if (this.hasSorFileInCloud) {
            return await this.exportSorFileFromCloud();
        }
        throw new Error("Cannot download a cloud measurement that is not a sor measurement");
    }
}

@autoinject
export class MeasurementFactory {
    constructor(
        private readonly measurementRepository: MeasurementRepository,
        private readonly measurementStorageService: IMeasurementStorageService,
        private readonly storageContainerService: StorageContainerService,
        private readonly storageService: IStorageService,
        private readonly recordingGroupContainerStorageService: RecordingGroupContainerStorageService
    ) {}

    public createOrphanMeasurement(localMeasurement: RecordingGroupContainer): OrphanMeasurement {
        const m = new OrphanMeasurement(
            this.measurementRepository,
            this.measurementStorageService,
            this.storageContainerService,
            this.storageService,
            this.recordingGroupContainerStorageService
        );
        m.setLocalContainer(localMeasurement);
        return m;
    }

    public createLocalMeasurement(
        localMeasurement: RecordingGroupContainer,
        cloudMeasurement: GenericMeasurement
    ): LocalMeasurement {
        const m = new LocalMeasurement(
            this.measurementRepository,
            this.measurementStorageService,
            this.storageContainerService,
            this.storageService,
            this.recordingGroupContainerStorageService
        );
        m.setLocalContainer(localMeasurement);
        m.setCloudMeasurement(cloudMeasurement);
        return m;
    }

    public createBothMeasurement(
        localMeasurement: RecordingGroupContainer,
        cloudMeasurement: GenericMeasurement
    ): BothMeasurement {
        const m = new BothMeasurement(
            this.measurementRepository,
            this.measurementStorageService,
            this.storageContainerService,
            this.storageService,
            this.recordingGroupContainerStorageService
        );
        m.setLocalContainer(localMeasurement);
        m.setCloudMeasurement(cloudMeasurement);
        return m;
    }

    public createCloudMeasurement(cloudMeasurement: GenericMeasurement): CloudMeasurement {
        const m = new CloudMeasurement(
            this.measurementRepository,
            this.measurementStorageService,
            this.storageContainerService,
            this.storageService,
            this.recordingGroupContainerStorageService
        );
        m.setCloudMeasurement(cloudMeasurement);
        return m;
    }

    public createRemoteMeasurement(cloudMeasurement: GenericMeasurement): RemoteMeasurement {
        const m = new RemoteMeasurement(
            this.measurementRepository,
            this.measurementStorageService,
            this.storageContainerService,
            this.storageService,
            this.recordingGroupContainerStorageService
        );
        m.setCloudMeasurement(cloudMeasurement);
        return m;
    }
}
